// OpenAI TTS wrapper — plays speech via Web Audio API instead of HTMLAudioElement.
// This bypasses iOS Safari's HTMLAudioElement autoplay restrictions entirely: once
// AudioContext.resume() is called inside a user gesture, the context stays active
// and any AudioBufferSourceNode can play at any time from any callback.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OPENAI_VOICES = [
    { id: "nova",    label: "Nova",    description: "warm · conversational · default" },
    { id: "shimmer", label: "Shimmer", description: "bright · cheerful" },
    { id: "coral",   label: "Coral",   description: "friendly · warm" },
    { id: "alloy",   label: "Alloy",   description: "neutral · balanced" },
    { id: "sage",    label: "Sage",    description: "wise · measured" },
    { id: "fable",   label: "Fable",   description: "expressive · storytelling" },
    { id: "echo",    label: "Echo",    description: "smooth · calm" },
    { id: "onyx",    label: "Onyx",    description: "deep · authoritative" },
    { id: "ash",     label: "Ash",     description: "clear · articulate" },
];

const AudioCtx =
    typeof window !== "undefined"
        ? (window.AudioContext || window.webkitAudioContext)
        : null;

export const ttsSupported = () => !!AudioCtx;

// ---------------------------------------------------------------------------
// AudioContext lifecycle
// ---------------------------------------------------------------------------
let ctx = null;
let audioUnlocked = false;
let pendingSpeak = null;   // { text, opts }
let currentSource = null;  // active AudioBufferSourceNode
let currentAbort = null;
let blockedListeners = new Set();
const UNLOCK_KEY = "meye_audio_unlocked";

const getCtx = () => {
    if (!AudioCtx) return null;
    if (!ctx) ctx = new AudioCtx();
    return ctx;
};

export const onVoiceBlocked = (cb) => {
    blockedListeners.add(cb);
    return () => blockedListeners.delete(cb);
};
const notifyBlocked = () => blockedListeners.forEach((cb) => cb());

/** Unlock the AudioContext inside a synchronous user-gesture handler.
 *  Returns true if the context is now running. Safe to call multiple times. */
export const unlockAudio = () => {
    const c = getCtx();
    if (!c) return false;
    // resume() must happen SYNCHRONOUSLY in the gesture handler on iOS Safari.
    // We don't await — the promise settles later.
    try {
        if (c.state === "suspended" || c.state === "interrupted") {
            c.resume().catch(() => {});
        }
        // Play a 1-sample silent buffer to fully engage the audio subsystem.
        const buf = c.createBuffer(1, 1, 22050);
        const src = c.createBufferSource();
        src.buffer = buf;
        src.connect(c.destination);
        try { src.start(0); } catch { /* ignore */ }

        // Consider unlocked if state is running now OR will be shortly.
        audioUnlocked = true;
        try { localStorage.setItem(UNLOCK_KEY, "1"); } catch { /* ignore */ }

        // If a speech was queued before unlock, play it now.
        if (pendingSpeak) {
            const q = pendingSpeak;
            pendingSpeak = null;
            speak(q.text, q.opts);
        }
        return true;
    } catch {
        return false;
    }
};

export const retryBlockedSpeech = () => {
    unlockAudio();
    if (pendingSpeak) {
        const q = pendingSpeak;
        pendingSpeak = null;
        return speak(q.text, q.opts);
    }
};

const stopCurrentSource = () => {
    if (currentSource) {
        try { currentSource.onended = null; } catch { /* ignore */ }
        try { currentSource.stop(0); } catch { /* ignore */ }
        currentSource = null;
    }
};

/**
 * Speak `text` using OpenAI's TTS via Web Audio API.
 * Returns a Promise that resolves when playback ends (or immediately on fallback/block).
 */
export const speak = async (text, { voiceName, speed = 1.0 } = {}) => {
    if (!ttsSupported() || !text) return;
    const clean = String(text).replace(/[*_`~]/g, "").trim();
    if (!clean) return;

    const c = getCtx();
    // Optimistic unlock: if the browser gave us a running context up-front
    // (common on desktop Chrome / Firefox), we don't need a gesture.
    if (!audioUnlocked && c && c.state === "running") {
        audioUnlocked = true;
    }

    // If AudioContext is still suspended (no gesture yet), queue and notify.
    if (!audioUnlocked) {
        pendingSpeak = { text: clean, opts: { voiceName, speed } };
        notifyBlocked();
        return;
    }

    stopSpeaking();

    const controller = new AbortController();
    currentAbort = controller;

    try {
        // Some browsers may auto-suspend the context after backgrounding — resume defensively.
        if (c.state === "suspended") {
            try { await c.resume(); } catch { /* ignore */ }
        }

        const res = await fetch(`${API}/tts`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                text: clean,
                voice: voiceName || "nova",
                speed,
            }),
            signal: controller.signal,
        });
        if (!res.ok) throw new Error(`tts http ${res.status}`);
        const arrayBuf = await res.arrayBuffer();
        if (controller.signal.aborted) return;

        // Decode MP3 → AudioBuffer. Support both Promise + callback forms (Safari).
        let audioBuffer;
        try {
            audioBuffer = await new Promise((resolve, reject) => {
                const maybe = c.decodeAudioData(arrayBuf.slice(0), resolve, reject);
                if (maybe && typeof maybe.then === "function") {
                    maybe.then(resolve, reject);
                }
            });
        } catch (decodeErr) {
            console.warn("Web Audio decode failed — falling back to HTMLAudio", decodeErr);
            return await playViaHtmlAudio(arrayBuf, controller);
        }
        if (controller.signal.aborted) return;

        return await new Promise((resolve) => {
            const src = c.createBufferSource();
            src.buffer = audioBuffer;
            src.connect(c.destination);
            let done = false;
            const finish = () => {
                if (done) return;
                done = true;
                if (currentSource === src) currentSource = null;
                resolve();
            };
            src.onended = finish;
            currentSource = src;
            try {
                src.start(0);
            } catch (e) {
                console.warn("AudioBufferSource.start failed, falling back", e);
                finish();
                playViaHtmlAudio(arrayBuf, controller);
            }
        });
    } catch (err) {
        if (err.name === "AbortError") return;
        console.warn("openai tts failed:", err.message);
    } finally {
        if (currentAbort === controller) currentAbort = null;
    }
};

// Fallback path — plain HTMLAudioElement for browsers that struggle with Web Audio API.
let fallbackAudio = null;
const playViaHtmlAudio = (arrayBuf, controller) =>
    new Promise((resolve) => {
        try {
            const blob = new Blob([arrayBuf], { type: "audio/mpeg" });
            const url = URL.createObjectURL(blob);
            if (!fallbackAudio) fallbackAudio = new Audio();
            fallbackAudio.src = url;
            const done = () => {
                URL.revokeObjectURL(url);
                fallbackAudio.onended = null;
                fallbackAudio.onerror = null;
                resolve();
            };
            fallbackAudio.onended = done;
            fallbackAudio.onerror = done;
            const p = fallbackAudio.play();
            if (p && p.catch) p.catch(done);
            if (controller?.signal) {
                controller.signal.addEventListener("abort", () => {
                    try { fallbackAudio.pause(); } catch { /* ignore */ }
                    done();
                });
            }
        } catch {
            resolve();
        }
    });

export const stopSpeaking = () => {
    if (currentAbort) {
        try { currentAbort.abort(); } catch { /* ignore */ }
        currentAbort = null;
    }
    stopCurrentSource();
    if (fallbackAudio) {
        try {
            fallbackAudio.pause();
            fallbackAudio.currentTime = 0;
        } catch { /* ignore */ }
    }
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
        try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    }
};

// Kept for backward compatibility.
export const getVoices = async () =>
    OPENAI_VOICES.map((v) => ({ name: v.id, lang: "en-US", label: v.label }));

// ---------------------------------------------------------------------------
// Sentence-pipelined speech
// ---------------------------------------------------------------------------
/** Split a chunk of text into ~1-sentence pieces. Short first pieces = faster time-to-first-audio. */
const splitSentences = (text) => {
    const clean = String(text || "").replace(/[*_`~]/g, "").trim();
    if (!clean) return [];
    // Split on sentence-final punctuation while preserving it. Merge tiny fragments back onto neighbours.
    const rough = clean.match(/[^.!?…]+[.!?…]+|[^.!?…]+$/g) || [clean];
    const out = [];
    for (const seg of rough) {
        const s = seg.trim();
        if (!s) continue;
        // If previous piece is short, merge; keeps sub-clauses of length <= 15 chars from spawning tiny TTS calls.
        if (out.length && out[out.length - 1].length + s.length < 40) {
            out[out.length - 1] = `${out[out.length - 1]} ${s}`;
        } else {
            out.push(s);
        }
    }
    return out;
};

/** Fetch + decode a single sentence's TTS mp3 into an AudioBuffer. */
const fetchSentenceBuffer = async (text, opts, controller) => {
    const c = getCtx();
    if (!c) return null;
    const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            text,
            voice: opts.voiceName || "nova",
            speed: opts.speed || 1.0,
        }),
        signal: controller.signal,
    });
    if (!res.ok) throw new Error(`tts http ${res.status}`);
    const arrayBuf = await res.arrayBuffer();
    if (controller.signal.aborted) return null;
    return await new Promise((resolve, reject) => {
        const maybe = c.decodeAudioData(arrayBuf.slice(0), resolve, reject);
        if (maybe && typeof maybe.then === "function") maybe.then(resolve, reject);
    });
};

let seqAbort = null;

/**
 * Speak `text` as a pipeline of sentences. Sentence #1 starts playing as soon as it's decoded,
 * while sentences #2+ are fetched + decoded in parallel. This slashes time-to-first-audio.
 *
 * Callbacks:
 *   opts.onSentenceStart(idx, sentence) — fires each time a new sentence begins playing
 *   opts.shouldAbort() — polled between sentences; if returns true, playback stops immediately
 */
export const speakSequence = async (text, opts = {}) => {
    if (!ttsSupported() || !text) return;
    const sentences = splitSentences(text);
    if (!sentences.length) return;

    const c = getCtx();
    if (!audioUnlocked && c && c.state === "running") audioUnlocked = true;
    if (!audioUnlocked) {
        pendingSpeak = { text, opts };
        notifyBlocked();
        return;
    }

    stopSpeaking();
    const controller = new AbortController();
    seqAbort = controller;
    currentAbort = controller;

    // Kick off ALL fetches in parallel so subsequent sentences are ready by the time we need them.
    const bufferPromises = sentences.map((s) =>
        fetchSentenceBuffer(s, opts, controller).catch((e) => {
            if (e.name !== "AbortError") console.warn("sentence tts failed", e);
            return null;
        })
    );

    try {
        if (c.state === "suspended") { try { await c.resume(); } catch { /* ignore */ } }
        for (let i = 0; i < sentences.length; i++) {
            if (controller.signal.aborted || opts.shouldAbort?.()) break;
            const buf = await bufferPromises[i];
            if (!buf) continue;
            if (controller.signal.aborted || opts.shouldAbort?.()) break;
            opts.onSentenceStart?.(i, sentences[i]);
            await new Promise((resolve) => {
                const src = c.createBufferSource();
                src.buffer = buf;
                src.connect(c.destination);
                let done = false;
                const finish = () => {
                    if (done) return;
                    done = true;
                    if (currentSource === src) currentSource = null;
                    resolve();
                };
                src.onended = finish;
                currentSource = src;
                controller.signal.addEventListener("abort", () => {
                    try { src.stop(0); } catch { /* ignore */ }
                    finish();
                });
                try { src.start(0); } catch { finish(); }
            });
        }
    } finally {
        if (seqAbort === controller) seqAbort = null;
        if (currentAbort === controller) currentAbort = null;
    }
};
